import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import useUser, { deleteAuthorized, getAuthorized, isUserDoctor, postAuthorized, putAuthorized } from "../../../hooks/User";
import SEO from "../../../components/SEO";
import { useLocation, useNavigate } from "react-router-dom";
import ProfileNavigation from "../../../components/common/ProfileNavigation";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { ROUTES } from "../../../routes";
import dayjs from "dayjs";
import { MiniTutorial, SURVEY_EDITOR_STEPS } from "../../../components/MiniTutorial";

import "./surveyeditor.css";

// Icons - using larger, bolder icons for better visibility
const ArrowUpIcon = () => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="22" 
        height="22" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="3" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ display: 'block' }}
    >
        <path d="M18 15l-6-6-6 6"/>
    </svg>
);

const ArrowDownIcon = () => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="22" 
        height="22" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="3" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ display: 'block' }}
    >
        <path d="M6 9l6 6 6-6"/>
    </svg>
);

const TrashIcon = () => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="20" 
        height="20" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="2.5" 
        strokeLinecap="round" 
        strokeLinejoin="round"
        style={{ display: 'block' }}
    >
        <path d="M3 6h18"/>
        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/>
        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/>
        <line x1="10" x2="10" y1="11" y2="17"/>
        <line x1="14" x2="14" y1="11" y2="17"/>
    </svg>
);

const PlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12h14"/>
        <path d="M12 5v14"/>
    </svg>
);

const SaveIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15.2 3a2 2 0 0 1 1.4.6l3.8 3.8a2 2 0 0 1 .6 1.4V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2z"/>
        <path d="M17 21v-7a1 1 0 0 0-1-1H8a1 1 0 0 0-1 1v7"/>
        <path d="M7 3v4a1 1 0 0 0 1 1h5"/>
    </svg>
);

const ImageIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="18" x="3" y="3" rx="2" ry="2"/>
        <circle cx="9" cy="9" r="2"/>
        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>
    </svg>
);

const MicIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 19c0 1.7-1.3 3-3 3s-3-1.3-3-3"/>
        <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
        <line x1="12" x2="12" y1="19" y2="22"/>
        <rect x="6" y="2" width="12" height="12" rx="6"/>
    </svg>
);

const ListIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <line x1="8" y1="6" x2="21" y2="6"/>
        <line x1="8" y1="12" x2="21" y2="12"/>
        <line x1="8" y1="18" x2="21" y2="18"/>
        <line x1="3" y1="6" x2="3.01" y2="6"/>
        <line x1="3" y1="12" x2="3.01" y2="12"/>
        <line x1="3" y1="18" x2="3.01" y2="18"/>
    </svg>
);

const LockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
);

const GlobeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20"/>
        <path d="M2 12h20"/>
    </svg>
);

const XIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18"/>
        <path d="m6 6 12 12"/>
    </svg>
);

const AlertIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <path d="m21 21-4.3-4.3"/>
    </svg>
);

// Status Config
const StatusConfig = {
    draft: { label: "Черновик", className: "status-draft", icon: "📝" },
    archived: { label: "Архив", className: "status-archived", icon: "📦" },
    active: { label: "Активный", className: "status-active", icon: "✅" }
};

const SurveyContext = createContext();

// Survey Context Provider
function SurveyContextProvider({ surveyId, children }) {
    const [id, setId] = useState(null);
    const [title, setTitle] = useState("");
    const [descr, setDescr] = useState("");
    const [status, setStatus] = useState("");
    const [quests, setQuests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(true);

    useEffect(() => {
        if (surveyId === null || surveyId === undefined) {
            setLoading(false);
            return;
        }

        if (!reload) return;

        setLoading(true);
        getAuthorized("/doctor/get_survey", { params: { survey_id: surveyId } })
            .then((response) => {
                const survey = response.data;
                setId(survey.id);
                setTitle(survey.title);
                setDescr(survey.description || "");
                setStatus(survey.status);
                setQuests(survey.questions || []);
            })
            .catch((error) => {
                console.error("Failed to load survey:", error);
            })
            .finally(() => {
                setLoading(false);
                setReload(false);
            });
    }, [surveyId, reload]);

    return (
        <SurveyContext.Provider value={{
            id, setId,
            title, setTitle,
            descr, setDescr,
            status, setStatus,
            quests, setQuests,
            loading,
            reload, setReload
        }}>
            {children}
        </SurveyContext.Provider>
    );
}

// Delete/Archive Confirmation Modal
function DeleteConfirmModal({ survey, isOpen, onClose, onConfirm }) {
    const [isDeleting, setIsDeleting] = useState(false);
    const { addNotification } = useContext(ErrorHandlerContext);
    const navigate = useNavigate();

    const handleDelete = useCallback(async () => {
        if (!survey) return;
        
        setIsDeleting(true);
        try {
            // Archive the survey (soft delete)
            await putAuthorized("/doctor/change_survey_status", {}, {
                params: {
                    survey_id: survey.id,
                    new_status: 'archived'
                }
            });
            onConfirm();
            onClose();
            // Navigate back to surveys list
            navigate(ROUTES.SURVEYS, { viewTransition: true });
        } catch (error) {
            console.error("Failed to delete survey:", error);
            addNotification(error.response?.data?.error_description || "Ошибка при удалении опроса");
        } finally {
            setIsDeleting(false);
        }
    }, [survey, onConfirm, onClose, addNotification, navigate]);

    if (!isOpen || !survey) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                <div className="confirm-icon">
                    <AlertIcon />
                </div>
                <h3>Удалить опрос?</h3>
                <p>
                    Вы уверены, что хотите удалить опрос <strong>"{survey.title}"</strong>?
                    <br />
                    Это действие нельзя отменить.
                </p>
                <div className="modal-actions">
                    <button 
                        className="btn-secondary"
                        onClick={onClose}
                        disabled={isDeleting}
                    >
                        Отмена
                    </button>
                    <button 
                        className="btn-danger"
                        onClick={handleDelete}
                        disabled={isDeleting}
                    >
                        {isDeleting ? "Удаление..." : "Удалить"}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Archive Button Component
function ArchiveButton({ onClick }) {
    return (
        <button 
            onClick={onClick}
            className="btn-archive"
            title="Удалить опрос"
        >
            <TrashIcon />
            Удалить
        </button>
    );
}

// Status Toggle Switch Component
function StatusToggle({ status, onChange }) {
    const options = [
        { key: 'draft', label: 'Черновик', icon: '📝' },
        { key: 'active', label: 'Активный', icon: '✅' }
    ];

    return (
        <div className="status-toggle-switch">
            {options.map((option) => (
                <button
                    key={option.key}
                    className={`toggle-option ${status === option.key ? 'active' : ''}`}
                    onClick={() => onChange(option.key)}
                    type="button"
                >
                    <span className="toggle-icon">{option.icon}</span>
                    <span>{option.label}</span>
                </button>
            ))}
        </div>
    );
}

// Survey Info Form Component
function SurveyInfoForm({ onSave }) {
    
    const { id, title, setTitle, descr, setDescr, status, setStatus } = useContext(SurveyContext);
    const { addNotification } = useContext(ErrorHandlerContext);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    
    // Track original values to detect what changed
    const originalValues = useRef({ title: title, descr: descr, status: status });
    
    // Update original values when survey loads/reloads
    useEffect(() => {
        originalValues.current = { title, descr, status };
        setHasChanges(false);
    }, [id]); // Reset when survey id changes (new survey loaded)

    useEffect(() => {
        const hasAnyChanges = 
            title !== originalValues.current.title ||
            descr !== originalValues.current.descr ||
            status !== originalValues.current.status;
        setHasChanges(hasAnyChanges);
    }, [title, descr, status]);

    const handleSave = useCallback(async () => {
        if (!title.trim()) {
            addNotification("Название опроса обязательно");
            return;
        }

        setIsSaving(true);
        try {
            await putAuthorized("/doctor/update_survey", {}, {
                params: {
                    title: title.trim(),
                    description: descr.trim(),
                    status,
                    survey_id: id
                }
            });
            
            // Determine what changed
            const titleChanged = title !== originalValues.current.title;
            const descrChanged = descr !== originalValues.current.descr;
            const statusChanged = status !== originalValues.current.status;
            
            // Update original values after successful save
            originalValues.current = { title, descr, status };
            setHasChanges(false);
            
            if (onSave) onSave();
            
            // Show appropriate message
            if (statusChanged && !titleChanged && !descrChanged) {
                addNotification({ msg: "Статус изменён", type: "success" });
            } else {
                addNotification({ msg: "Изменения сохранены", type: "success" });
            }
        } catch (error) {
            console.error("Failed to save survey:", error);
            addNotification(error.response?.data?.error_description || "Ошибка при сохранении");
        } finally {
            setIsSaving(false);
        }
    }, [title, descr, status, id, addNotification, onSave]);

    // Create survey object for delete modal
    const survey = { id, title };

    return (
        <div className="survey-info-card">
            <div className="survey-info-header">
                <h2>📋 Информация об опросе</h2>
                <div className="survey-id-badge">ID: {id}</div>
            </div>

            <div className="form-section">
                <label className="form-label">Название *</label>
                <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Введите название опроса"
                    className="form-input"
                />
            </div>

            <div className="form-section">
                <label className="form-label">Описание</label>
                <textarea
                    value={descr}
                    onChange={(e) => setDescr(e.target.value)}
                    placeholder="Добавьте описание опроса (необязательно)"
                    rows={4}
                    className="form-textarea"
                />
            </div>

            <div className="form-section">
                <label className="form-label">Статус</label>
                <StatusToggle status={status} onChange={setStatus} />
            </div>

            <div className="survey-info-actions">
                <button 
                    onClick={handleSave} 
                    disabled={isSaving || !hasChanges}
                    className="btn-primary"
                >
                    <SaveIcon />
                    {isSaving ? "Сохранение..." : "Сохранить изменения"}
                </button>
                <ArchiveButton onClick={() => setIsDeleteModalOpen(true)} />
            </div>

            <DeleteConfirmModal
                survey={survey}
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={() => {}}
            />
        </div>
    );
}

// Question Item Component
function QuestionItem({ question, index, totalCount }) {
    const { id: surveyId, setReload } = useContext(SurveyContext);
    const { addNotification } = useContext(ErrorHandlerContext);
    const [isRemoving, setIsRemoving] = useState(false);

    const handleMoveUp = useCallback(async () => {
        if (index === 0) return;
        try {
            await putAuthorized("/doctor/swap_questions_in_survey", {}, {
                params: {
                    survey_id: surveyId,
                    order_index_1: question.order_index,
                    order_index_2: question.order_index - 1
                }
            });
            setReload(true);
        } catch (error) {
            console.error("Failed to move question:", error);
            addNotification("Ошибка при перемещении вопроса");
        }
    }, [surveyId, question.order_index, index, setReload, addNotification]);

    const handleMoveDown = useCallback(async () => {
        if (index === totalCount - 1) return;
        try {
            await putAuthorized("/doctor/swap_questions_in_survey", {}, {
                params: {
                    survey_id: surveyId,
                    order_index_1: question.order_index,
                    order_index_2: question.order_index + 1
                }
            });
            setReload(true);
        } catch (error) {
            console.error("Failed to move question:", error);
            addNotification("Ошибка при перемещении вопроса");
        }
    }, [surveyId, question.order_index, index, totalCount, setReload, addNotification]);

    const handleRemove = useCallback(async () => {
        setIsRemoving(true);
        try {
            await deleteAuthorized("/doctor/remove_question_from_survey", {}, {
                params: {
                    survey_id: surveyId,
                    question_in_survey_id: question.question_in_survey_id
                }
            });
            setReload(true);
            addNotification({ msg: "Вопрос удалён", type: "success" });
        } catch (error) {
            console.error("Failed to remove question:", error);
            addNotification("Ошибка при удалении вопроса");
        } finally {
            setIsRemoving(false);
        }
    }, [surveyId, question.question_in_survey_id, setReload, addNotification]);

    const hasImage = question.picture_filename != null;
    const hasVoice = question.voice_filename != null;
    const answerCount = question.answer_options?.length || 0;

    return (
        <div className={`question-item ${isRemoving ? 'removing' : ''}`}>
            <div className="question-number">{index + 1}</div>
            
            <div className="question-content">
                <div className="question-header">
                    <span className={`question-visibility ${question.is_public ? 'public' : 'private'}`}>
                        {question.is_public ? <><GlobeIcon /> Публичный</> : <><LockIcon /> Приватный</>}
                    </span>
                    <div className="question-badges">
                        {hasImage && <span className="question-badge image" title="Есть изображение"><ImageIcon /></span>}
                        {hasVoice && <span className="question-badge voice" title="Есть голос"><MicIcon /></span>}
                        {answerCount > 0 && (
                            <span className="question-badge options" title={`${answerCount} вариантов ответа`}>
                                <ListIcon />
                                {answerCount}
                            </span>
                        )}
                    </div>
                </div>
                
                <p className="question-text">{question.text}</p>
            </div>

            <div className="question-actions">
                <button 
                    className="action-btn move-up" 
                    onClick={handleMoveUp}
                    disabled={index === 0}
                    title="Переместить вверх"
                >
                    <ArrowUpIcon />
                </button>
                <button 
                    className="action-btn move-down" 
                    onClick={handleMoveDown}
                    disabled={index === totalCount - 1}
                    title="Переместить вниз"
                >
                    <ArrowDownIcon />
                </button>
                <button 
                    className="action-btn delete" 
                    onClick={handleRemove}
                    disabled={isRemoving}
                    title="Удалить из опроса"
                >
                    <TrashIcon />
                </button>
            </div>
        </div>
    );
}

// Add Question Modal
function AddQuestionModal({ isOpen, onClose }) {
    const { id: surveyId, quests, setReload } = useContext(SurveyContext);
    const { addNotification } = useContext(ErrorHandlerContext);
    const [availableQuestions, setAvailableQuestions] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [loading, setLoading] = useState(true);
    const [addingIds, setAddingIds] = useState(new Set());
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const limit = 10;

    // Debounced search query
    const [debouncedQuery, setDebouncedQuery] = useState("");

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedQuery(searchQuery);
            setCurrentPage(1); // Reset to first page on search
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        if (!isOpen) return;

        setLoading(true);
        const params = {
            st: (currentPage - 1) * limit,
            limit: limit
        };
        
        // Add search query if present
        if (debouncedQuery.trim()) {
            params.query = debouncedQuery.trim();
        }

        getAuthorized("/doctor/get_available_questions", { params })
            .then((response) => {
                setAvailableQuestions(response.data.questions || []);
                setTotalCount(response.data.total_count || 0);
            })
            .catch((error) => {
                console.error("Failed to load questions:", error);
                addNotification("Ошибка при загрузке вопросов");
            })
            .finally(() => {
                setLoading(false);
            });
    }, [isOpen, debouncedQuery, currentPage, addNotification]);

    const handleAdd = useCallback(async (question) => {
        if (addingIds.has(question.id)) return;

        setAddingIds(prev => new Set(prev).add(question.id));
        try {
            await postAuthorized("/doctor/add_question_to_survey", {}, {
                params: {
                    question_id: question.id,
                    survey_id: surveyId
                }
            });
            setReload(true);
            addNotification({ msg: "Вопрос добавлен", type: "success" });
            // Don't close modal, allow adding more questions
        } catch (error) {
            console.error("Failed to add question:", error);
            addNotification(error.response?.data?.error_description || "Ошибка при добавлении вопроса");
        } finally {
            setAddingIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(question.id);
                return newSet;
            });
        }
    }, [surveyId, setReload, addNotification, addingIds]);

    const isAlreadyAdded = useCallback((questionId) => {
        return quests.some(q => q.id === questionId);
    }, [quests]);

    const totalPages = Math.ceil(totalCount / limit) || 1;
    const hasNext = currentPage < totalPages;
    const hasPrev = currentPage > 1;

    const handleNextPage = () => {
        if (hasNext) setCurrentPage(prev => prev + 1);
    };

    const handlePrevPage = () => {
        if (hasPrev) setCurrentPage(prev => prev - 1);
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Добавить вопросы</h2>
                    <button className="modal-close" onClick={onClose}>
                        <XIcon />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="search-bar">
                        <SearchIcon />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Поиск по вопросам..."
                        />
                    </div>

                    {loading ? (
                        <div className="loading-state">Загрузка вопросов...</div>
                    ) : availableQuestions.length === 0 ? (
                        <div className="empty-state">
                            {debouncedQuery ? "Вопросы не найдены" : "Нет доступных вопросов"}
                        </div>
                    ) : (
                        <>
                            <div className="available-questions-list">
                                {availableQuestions.map((question) => {
                                    const alreadyAdded = isAlreadyAdded(question.id);
                                    const isAdding = addingIds.has(question.id);
                                    const hasImage = question.picture_filename != null;
                                    const hasVoice = question.voice_filename != null;
                                    const answerCount = question.answer_options?.length || 0;

                                    return (
                                        <div key={question.id} className={`available-question-item ${alreadyAdded ? 'added' : ''}`}>
                                            <div className="question-info">
                                                <p className="question-text">{question.text}</p>
                                                <div className="question-meta">
                                                    <span className={question.is_public ? 'public' : 'private'}>
                                                        {question.is_public ? 'Публичный' : 'Приватный'}
                                                    </span>
                                                    {hasImage && <span className="badge"><ImageIcon /></span>}
                                                    {hasVoice && <span className="badge"><MicIcon /></span>}
                                                    {answerCount > 0 && (
                                                        <span className="badge">
                                                            <ListIcon /> {answerCount}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                className="btn-add"
                                                onClick={() => handleAdd(question)}
                                                disabled={alreadyAdded || isAdding}
                                            >
                                                {alreadyAdded ? '✓ Добавлен' : isAdding ? 'Добавление...' : 'Добавить'}
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                            
                            {/* Pagination */}
                            {totalCount > limit && (
                                <div className="modal-pagination">
                                    <button 
                                        className="btn-page" 
                                        onClick={handlePrevPage} 
                                        disabled={!hasPrev || loading}
                                    >
                                        ← Назад
                                    </button>
                                    <span className="page-info">
                                        Страница {currentPage} из {totalPages} ({totalCount} всего)
                                    </span>
                                    <button 
                                        className="btn-page" 
                                        onClick={handleNextPage} 
                                        disabled={!hasNext || loading}
                                    >
                                        Вперёд →
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>
                        Закрыть
                    </button>
                </div>
            </div>
        </div>
    );
}

// Questions List Component
function QuestionsList() {
    const { quests, setReload, loading } = useContext(SurveyContext);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);

    if (loading) {
        return (
            <div className="survey-questions-card">
                <div className="loading-state">Загрузка опроса...</div>
            </div>
        );
    }

    return (
        <div className="survey-questions-card">
            <div className="questions-header">
                <div>
                    <h2>❓ Вопросы опроса</h2>
                    <p className="questions-subtitle">
                        {quests.length > 0 
                            ? `Всего вопросов: ${quests.length}` 
                            : 'Добавьте вопросы к опросу'
                        }
                    </p>
                </div>
                <button className="btn-primary" onClick={() => setIsAddModalOpen(true)}>
                    <PlusIcon />
                    Добавить вопрос
                </button>
            </div>

            {quests.length === 0 ? (
                <div className="empty-questions-state">
                    <div className="empty-icon">📝</div>
                    <h3>Нет вопросов</h3>
                    <p>Добавьте вопросы, чтобы пациенты могли пройти опрос</p>
                    <button className="btn-primary" onClick={() => setIsAddModalOpen(true)}>
                        <PlusIcon />
                        Добавить первый вопрос
                    </button>
                </div>
            ) : (
                <div className="questions-list">
                    {quests.map((question, index) => (
                        <QuestionItem
                            key={question.question_in_survey_id}
                            question={question}
                            index={index}
                            totalCount={quests.length}
                        />
                    ))}
                </div>
            )}

            <AddQuestionModal 
                isOpen={isAddModalOpen} 
                onClose={() => setIsAddModalOpen(false)} 
            />
        </div>
    );
}

// Main Survey Editor Component
function SurveyEditor() {
    const { loading } = useContext(SurveyContext);

    if (loading) {
        return (
            <div className="survey-editor-loading">
                <div className="spinner"></div>
                <p>Загрузка опроса...</p>
            </div>
        );
    }

    return (
        <div className="survey-editor-container">
            <div className="survey-editor-grid">
                <div className="survey-editor-main">
                    <SurveyInfoForm />
                </div>
                <div className="survey-editor-sidebar">
                    <QuestionsList />
                </div>
            </div>
            <MiniTutorial steps={SURVEY_EDITOR_STEPS} pageKey="survey-editor" />
        </div>
    );
}

// Main Page Component
function SurveyEditorPage() {
    const { data, loading: userLoading, error } = useUser();
    const navigate = useNavigate();
    const location = useLocation();
    const [surveyId, setSurveyId] = useState(null);

    useEffect(() => {
        if (userLoading) return;
        if (error) navigate(ROUTES.AUTH, { viewTransition: true });
        if (!isUserDoctor(data)) navigate(ROUTES.PROFILE, { viewTransition: true });

        const id = location?.state?.surveyId;
        if (id === null || id === undefined) {
            navigate(ROUTES.SURVEYS, { viewTransition: true });
            return;
        }

        setSurveyId(id);
    }, [userLoading, error, data, location, navigate]);

    if (userLoading) {
        return (
            <>
                <ProfileNavigation isDoctor={true} />
                <div className="survey-editor-loading">
                    <div className="spinner"></div>
                </div>
            </>
        );
    }

    return (
        <>
            <ProfileNavigation isDoctor={isUserDoctor(data)} />
            <SurveyContextProvider surveyId={surveyId}>
                <SurveyEditor />
            </SurveyContextProvider>
        </>
    );
}

// Export wrapped with ErrorHandler
export default function SurveyEditorPageWithErrorHandler() {
    return (<>
        <SEO title="Редактор опроса" description="Редактирование вопросов и настроек медицинского опроса" noindex={true} />
        <ErrorHandlerContextProvider>
            <SurveyEditorPage />
            <ErrorHandler />
        </ErrorHandlerContextProvider>
    </>);
}
